"use client"

import styled from "@emotion/styled"
import { Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { ElementType } from "react"
import { colors, mediaQueries, sizes } from "ui"
import { getNonUmbracoUrl } from "utils"
import ButtonWithArrow from "./bit/ButtonWithArrow"
import IconWithBackground from "./bit/IconWithBackground"

const StyledLinkCard = styled(ButtonWithArrow, {
  shouldForwardProp: (prop) => prop !== "isLink",
})<{ isLink?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: auto;
  min-height: fit-content;
  border-radius: ${sizes.borderRadius};
  padding: 12px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: -4.297px 8.593px 32.225px 0px rgba(2, 1, 64, 0.12);
  ${(props) => !props.isLink && `cursor: default;`}
  gap: ${sizes.baseDouble};
  text-decoration: none;
  ${mediaQueries.desktopM} {
    flex-direction: column;
    padding: ${sizes.baseTriple};
    gap: ${sizes.baseSingle};
  }
  ${mediaQueries.desktopXl} {
    gap: ${sizes.baseDouble};
  }
  svg {
    margin-inline-start: 0;
    object-fit: contain;
    width: unset;
    ${(props) => !props.isLink && `display: none;`}
  }
`

const StyledText = styled(Typography)`
  text-align: center;
`

interface ActionLinkCardData {
  linkUrl?: string
  linkTarget?: string
  onClick?: () => void
  icon?: string
  title?: string
  text?: string
  primaryColor?: string
  id?: string
  index?: number
}

interface ActionLinkCardProps {
  data: ActionLinkCardData
  uniqueKeyPrefix?: string
  className?: string
  titleComponent?: ElementType
}

const ActionLinkCard = ({
  data,
  className,
  uniqueKeyPrefix = "card",
  titleComponent = "h3",
}: ActionLinkCardProps) => {
  const {
    linkUrl,
    linkTarget,
    onClick,
    icon,
    title,
    text,
    primaryColor = colors.red,
    id,
    index = 0,
  } = data

  const t = useTranslations("SEO")
  const uniqueKey = `${uniqueKeyPrefix}-${id || ""}-${index}-action-link-card`
  const shotLinkArrow = linkUrl === "/" ? false : linkUrl ? true : false

  return (
    <StyledLinkCard
      className={className}
      href={
        linkUrl === "/"
          ? undefined
          : linkUrl
            ? getNonUmbracoUrl(linkUrl)
            : undefined
      }
      variant="elevated"
      ArrowWithLine={false}
      colorPallette={
        (colors.resolveColorName("#" + primaryColor) as any) || "red"
      }
      target={linkTarget}
      key={uniqueKey}
      onClick={onClick}
      isLink={shotLinkArrow}
    >
      {icon && (
        <IconWithBackground
          size={48}
          icon={<Image src={icon} alt={`${t("iconAlt")}${title}`} fill />}
          backgroundColor={primaryColor}
        />
      )}
      {title && (
        <Typography
          variant="h5"
          component={titleComponent}
          color={colors.black}
        >
          {title}
        </Typography>
      )}
      {text && (
        <StyledText variant="body1" color={colors.red}>
          {text}
        </StyledText>
      )}
    </StyledLinkCard>
  )
}

export default ActionLinkCard
